import { useTranslation } from 'react-i18next';
import { ScrollText } from 'lucide-react';
import { Badge, Dialog, DialogShell, DialogShellBody, DialogShellHeader } from '@bliks-io/ui';
import { SYSTEM_ACTOR_ID, type AuditLog, type AuditLogActor } from '@bliks-io/contracts';
import { formatDateTime } from '@/lib/formatters';
import { ChangesDetail } from './ChangesDetail';
import { ACTION_VARIANTS, getActionLabel, getEntityTypeLabel } from './auditLogs.constants';

interface AuditLogDetailDialogProps {
  log: AuditLog;
  actors: Record<string, AuditLogActor> | undefined;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AuditLogDetailDialog({
  log,
  actors,
  open,
  onOpenChange
}: AuditLogDetailDialogProps) {
  const { t } = useTranslation();

  const action = log.action;
  const isSystemActor = log.actorId === SYSTEM_ACTOR_ID;
  const actor = isSystemActor ? null : actors?.[log.actorId];

  let actorDisplay;
  if (isSystemActor) {
    actorDisplay = <span className="italic">{t('auditLogs.systemActor')}</span>;
  } else if (actor) {
    actorDisplay = (
      <span title={actor.email}>
        {actor.firstName} {actor.lastName}
      </span>
    );
  } else {
    actorDisplay = <span className="font-mono">{log.actorId}</span>;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogShell size="md">
        <DialogShellHeader
          icon={<ScrollText className="h-5 w-5" />}
          title={getEntityTypeLabel(t, log.entityType)}
          subtitle={
            <span className="flex flex-wrap items-center gap-2">
              <Badge variant={ACTION_VARIANTS[action] ?? 'outline'}>
                {getActionLabel(t, action)}
              </Badge>
              {log.entityId && <span className="font-mono">{log.entityId}</span>}
            </span>
          }
          onClose={() => onOpenChange(false)}
        />
        <DialogShellBody>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm border-b pb-3">
            <div>
              <span className="text-muted-foreground">{t('auditLogs.timestamp')}:</span>{' '}
              {formatDateTime(log.timestamp)}
            </div>
            <div>
              <span className="text-muted-foreground">{t('auditLogs.actor')}:</span> {actorDisplay}
            </div>
            {log.metadata?.ip && (
              <div>
                <span className="text-muted-foreground">{t('auditLogs.ip')}:</span>{' '}
                <span className="font-mono">{log.metadata.ip}</span>
              </div>
            )}
            {log.metadata?.endpoint && (
              <div>
                <span className="text-muted-foreground">{t('auditLogs.endpoint')}:</span>{' '}
                <span className="font-mono">{log.metadata.endpoint}</span>
              </div>
            )}
            {log.sessionId && (
              <div className="col-span-2">
                <span className="text-muted-foreground">{t('auditLogs.sessionId')}:</span>{' '}
                <span className="font-mono text-xs">{log.sessionId}</span>
              </div>
            )}
          </div>

          <ChangesDetail log={log} />
        </DialogShellBody>
      </DialogShell>
    </Dialog>
  );
}
